import Experience from "@folio-2026/experience"
import { MQ, MSDFTextGeometry, MSDFTextNodeMaterial } from "@folio-2026/shared"
import gsap from "gsap"
import { add, attribute, mix, step, uniform } from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import { MSDFFont } from "../../text/lib/Text"

const mm = gsap.matchMedia()

export default class MemoryText {
  experience: Experience
  scene: THREE.Scene
  debugFolder?: FolderApi

  geometry!: MSDFTextGeometry
  material!: MSDFTextNodeMaterial
  mesh!: THREE.Mesh
  text: string = ""
  progressUniform!: ReturnType<typeof uniform>
  visibleWorlds: number = 0
  visibleWorldsUniform!: ReturnType<typeof uniform>

  private updateCounter: number = 0
  private lastScrambledText: string = ""

  settings = {
    scale: 0.3 / 1000,
  }

  constructor({
    scene,
    debugFolder,
  }: {
    scene: THREE.Scene
    debugFolder?: FolderApi
  }) {
    this.experience = Experience.getInstance()
    this.scene = scene
    this.debugFolder = debugFolder

    this.createMessageText()
    this.setDebug()
  }

  createMessageText() {
    const font = this.experience.globalResources
      .bmfontUbuntuMonoRegular as MSDFFont
    const atlas = this.experience.globalResources
      .atlasUbuntuSansMonoRegular as THREE.Texture

    if (!font || !font.data) {
      console.error("MemoryText: Font not loaded", font)
      return
    }

    if (!atlas) {
      console.error("MemoryText: Atlas texture not loaded", atlas)
      return
    }

    this.geometry = new MSDFTextGeometry({
      text: "",
      font: font.data,
      lineHeight: 20,
      width: 350,
      align: "center",
    })

    if (!this.geometry) {
      console.error("MemoryText: Geometry not created properly", this.geometry)
      return
    }

    const progressUniform = uniform(1)
    const visibleWorldsUniform = uniform(0)

    const colorUniform = uniform(new THREE.Color("#4080a0"))

    this.material = new MSDFTextNodeMaterial({
      isSmooth: 1,
      strokeOutsetWidth: 0,
      strokeInsetWidth: 0,
      threshold: 0.5,
      map: atlas,
      fade: uniform(false),
      progress: progressUniform,
      color: colorUniform as unknown as THREE.ConstNode<THREE.Color>,
      noiseTexture: this.experience.globalResources.perlin as THREE.Texture,
      noiseUvFrequency: 0,
      noiseUvAmplitude: 0,
    })

    this.progressUniform = progressUniform
    this.visibleWorldsUniform = visibleWorldsUniform

    // Modify colorNode to apply darker color to scrambled words
    const originalColor = this.material.color
    const wordIndexAttr = attribute("wordIndex", "float")
    // step(a, b) returns 1 if b >= a, 0 otherwise
    // We want isScrambled = 1 when wordIndex > visibleWorlds
    // So we use step(visibleWorlds + 1, wordIndex) which returns 1 when wordIndex >= visibleWorlds + 1
    const isScrambled = step(add(visibleWorldsUniform, 1), wordIndexAttr)
    const darkerColor = originalColor.mul(0.2) // 50% brightness for scrambled words
    this.material.colorNode = mix(originalColor, darkerColor, isScrambled)

    this.material.side = THREE.DoubleSide

    this.mesh = new THREE.Mesh(this.geometry, this.material)

    // Set scale based on screen size - larger on mobile for better readability
    mm.add(
      {
        lgUp: MQ.lgUp,
        lgDown: MQ.lgDown,
      },
      (context) => {
        const { lgUp } = context.conditions as { lgUp: boolean }
        // Desktop scale: 0.3 / 1000, Mobile scale: 0.45 / 1000 (50% larger)
        this.settings.scale = lgUp ? 0.3 / 1000 : 0.45 / 1000
        this.mesh.scale.set(
          this.settings.scale,
          this.settings.scale,
          this.settings.scale,
        )
      },
    )

    this.mesh.position.set(0, 2.9, 3)

    this.mesh.rotateX(Math.PI)

    this.mesh.visible = false
    this.mesh.frustumCulled = false

    this.scene.add(this.mesh)
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "⬛ MemoryText",
      expanded: false,
    })

    const position = {
      x: this.mesh.position.x,
      y: this.mesh.position.y,
      z: this.mesh.position.z,
    }

    folder
      .addBinding(position, "x", {
        label: "Position X",
        min: -10,
        max: 10,
        step: 0.001,
      })
      .on("change", (ev) => {
        this.mesh.position.x = ev.value
      })

    folder
      .addBinding(position, "y", {
        label: "Position Y",
        min: 2,
        max: 4,
        step: 0.001,
      })
      .on("change", (ev) => {
        this.mesh.position.y = ev.value
      })

    folder
      .addBinding(position, "z", {
        label: "Position Z",
        min: -20,
        max: 20,
        step: 0.1,
      })
      .on("change", (ev) => {
        this.mesh.position.z = ev.value
      })

    const visible = {
      value: this.mesh.visible,
    }

    folder
      .addBinding(visible, "value", {
        label: "Visible",
      })
      .on("change", (ev) => {
        this.mesh.visible = ev.value
      })

    const progress = {
      value: this.progressUniform.value,
    }

    folder
      .addBinding(progress, "value", {
        label: "Progress",
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        const progressValue = ev.value as number
        this.progressUniform.value = progressValue
        // Update scrambled text based on progress
        if (this.text) {
          if (progressValue >= 1) {
            // Show actual text when progress is complete
            // Set visibleWorlds to a high value so all words are visible (not scrambled)
            const tokens = this.text.split(/(\s+|\n+)/)
            const wordCount = Math.ceil(tokens.length / 2)
            this.visibleWorlds = wordCount
            this.visibleWorldsUniform.value = wordCount

            this.geometry.update({
              text: this.text,
            })
          } else {
            // Show scrambled text during animation
            // textScramble will update visibleWorlds and visibleWorldsUniform
            const scrambledText = this.textScramble(this.text, progressValue)
            this.geometry.update({
              text: scrambledText,
            })
          }
        }
      })

    const scaleSettings = {
      value: this.settings.scale * 1000,
    }

    folder
      .addBinding(scaleSettings, "value", {
        label: "Scale",
        min: 0.001,
        max: 1,
        step: 0.001,
      })
      .on("change", (ev) => {
        const newScale = ev.value / 1000
        this.settings.scale = newScale
        this.mesh.scale.set(newScale, newScale, newScale)
      })

    const colorUniform = this.material.color as THREE.UniformNode<THREE.Color>
    const textColorSettings = {
      color: "#" + colorUniform.value.getHexString(),
    }

    folder
      .addBinding(textColorSettings, "color", {
        label: "Text color",
        view: "color",
      })
      .on("change", (ev) => {
        colorUniform.value = new THREE.Color(ev.value)
      })
  }

  textScramble(text: string, progress: number) {
    // Split by spaces and newlines, preserving delimiters
    const tokens = text.split(/(\s+|\n+)/)
    const wordCount = Math.ceil(tokens.length / 2)
    const visibleWorlds = wordCount - Math.ceil(wordCount * (1 - progress))

    // Store visibleWorlds and update uniform
    this.visibleWorlds = visibleWorlds
    this.visibleWorldsUniform.value = visibleWorlds

    const chars = "jompaWB)#@$"
    const charsLength = chars.length
    let wordIndex = 0
    let scrubled = ""

    // Single pass: process tokens directly
    for (let i = 0; i < tokens.length; i++) {
      if (i % 2 === 0) {
        const world = tokens[i] // words
        const delimiter = tokens[i + 1] || ""

        if (wordIndex <= visibleWorlds) {
          scrubled += world + delimiter
        } else {
          // Random word
          const wordLength = world.length
          let randomWord = ""
          for (let j = 0; j < wordLength; j++) {
            randomWord += chars[Math.floor(Math.random() * charsLength)]
          }
          scrubled += randomWord + delimiter
        }
        wordIndex++
      }
    }

    return scrubled
  }

  reveal() {
    if (!this.text) {
      return
    }

    const proxy = { progress: 0 }

    this.updateCounter = 0
    this.lastScrambledText = ""

    gsap.to(proxy, {
      progress: 1,
      duration: 1,
      ease: "none",

      onUpdate: () => {
        this.updateCounter++
        // Only call textScramble every 5 onUpdate calls (or on first call)
        if (this.updateCounter % 5 === 0 || this.lastScrambledText === "") {
          this.lastScrambledText = this.textScramble(this.text, proxy.progress)
        }

        this.geometry.update({
          text: this.lastScrambledText,
        })
      },
      onComplete: () => {
        // Set visibleWorlds to a high value so all words are visible (not scrambled)
        const tokens = this.text.split(/(\s+|\n+)/)
        const wordCount = Math.ceil(tokens.length / 2)
        this.visibleWorlds = wordCount
        this.visibleWorldsUniform.value = wordCount

        this.geometry.update({
          text: this.text,
        })
      },
    })

    const fadeProxy = { progress: 0 }

    gsap.to(fadeProxy, {
      progress: 1,
      duration: 1,
      ease: "power2.out",

      onUpdate: () => {
        this.progressUniform.value = fadeProxy.progress
      },
    })
  }

  leave() {
    if (!this.text) {
      return
    }

    const proxy = { progress: 1 }

    this.updateCounter = 0
    this.lastScrambledText = this.text

    gsap.to(proxy, {
      progress: 0,
      duration: 1,
      ease: "none",

      onUpdate: () => {
        this.updateCounter++
        // Only call textScramble every 5 onUpdate calls (or on first call)
        if (
          this.updateCounter % 5 === 0 ||
          this.lastScrambledText === this.text
        ) {
          this.lastScrambledText = this.textScramble(this.text, proxy.progress)
        }

        this.geometry.update({
          text: this.lastScrambledText,
        })
      },
      onComplete: () => {
        // Set visibleWorlds to 0 so all words are scrambled
        this.visibleWorlds = 0
        this.visibleWorldsUniform.value = 0

        this.geometry.update({
          text: "",
        })
      },
    })

    const fadeProxy = { progress: 1 }

    gsap.to(fadeProxy, {
      progress: 0,
      duration: 1,
      ease: "power2.in",

      onUpdate: () => {
        this.progressUniform.value = fadeProxy.progress
      },
    })
  }
}
